import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Pagination,
  CircularProgress,
  Stack,
  Button,
  Typography,
  Grid,
} from '@mui/material';

import { Add } from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';

import { useAuth } from '../../contexts/AuthContext';

import VideoCard from './GuideVideoCard';
import VideoFilters from './GuideVideoFilters';
import UploadVideoModal from './UploadGuideVideoModal';
import ConfirmDialog from '../../components/base/ConfirmDialog';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';

import {
  arrayMove,
  SortableContext,
  rectSortingStrategy
} from '@dnd-kit/sortable';

const GuideVideos = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [openModal, setOpenModal] = useState(false);
  const [videoToEdit, setVideoToEdit] = useState(null);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);

  const rowsPerPage = 12;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // =========================
  // FETCH
  // =========================
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/usage-guides/videos`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setVideos(res.data.data || []);
    } catch {
      toast.error('Erreur chargement vidéos');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchData();
  }, [fetchData, token]);

  // =========================
  // FILTER
  // =========================
  const filteredVideos = useMemo(() => {
    const term = search.toLowerCase();
    return videos.filter(v =>
      (v.title || '').toLowerCase().includes(term) ||
      (v.description || '').toLowerCase().includes(term)
    );
  }, [videos, search]);

  const currentVideos = filteredVideos.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // =========================
  // DRAG END (FIX)
  // =========================
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = videos.findIndex(v => v.id === active.id);
    const newIndex = videos.findIndex(v => v.id === over.id);

    const newList = arrayMove(videos, oldIndex, newIndex);

    // 🔥 update local order immediately
    setVideos(newList);

    const payload = newList.map((v, index) => ({
      id: v.id,
      position: index,
    }));

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/usage-guides/videos/positions`,
        { videos: payload },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Ordre mis à jour');
    } catch {
      toast.error('Erreur update position');
    }
  };

  // =========================
  // DELETE
  // =========================
  const handleDeleteClick = (id) => {
    setVideoToDelete(id);
    setOpenConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/usage-guides/videos/${videoToDelete}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('Vidéo supprimée');
      setOpenConfirm(false);
      fetchData();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleEdit = (video) => {
    setVideoToEdit(video);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setVideoToEdit(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 5, bgcolor: '#F4F7FE', minHeight: '100vh' }}>

      <VideoFilters onSearchChange={setSearch}>
        <Button startIcon={<Add />} onClick={() => setOpenModal(true)}>
          Publier un guide
        </Button>
      </VideoFilters>
{/* INFO DRAG & DROP */}
<Box
  sx={{
    mb: 3,
    p: 2,
    borderRadius: "12px",
    bgcolor: "#fff",
    border: "1px dashed #4318FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  }}
>
  <Stack>
    <Typography fontWeight={800} sx={{ color: "#1B2559" }}>
      Réorganisation des vidéos
    </Typography>

    <Typography variant="body2" sx={{ color: "#707EAE" }}>
      Glissez-déposez les cartes pour ajuster l’ordre d’affichage (position)
    </Typography>
  </Stack>

  <Typography
    sx={{
      fontSize: "12px",
      fontWeight: 700,
      color: "#4318FF",
      bgcolor: "#F4F0FF",
      px: 2,
      py: 0.5,
      borderRadius: "20px"
    }}
  >
    Drag & Drop actif
  </Typography>
</Box>
      {/* 🔥 DRAG CONTEXT FIX */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={videos.map(v => v.id)}
          strategy={rectSortingStrategy}
        >

          <Grid container spacing={4}>
            {currentVideos.map(video => (
              <Grid item xs={12} sm={6} md={4} key={video.id}>
                <VideoCard
                  video={video}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              </Grid>
            ))}
          </Grid>

        </SortableContext>
      </DndContext>

      {filteredVideos.length > rowsPerPage && (
        <Stack alignItems="center" mt={4}>
          <Pagination
            count={Math.ceil(filteredVideos.length / rowsPerPage)}
            page={page}
            onChange={(e, v) => setPage(v)}
          />
        </Stack>
      )}

      <UploadVideoModal
        open={openModal}
        onClose={handleCloseModal}
        onRefresh={fetchData}
        user={user}
        videoToEdit={videoToEdit}
      />

      <ConfirmDialog
        open={openConfirm}
        title="Supprimer"
        message="Confirmer suppression ?"
        onConfirm={handleConfirmDelete}
        onCancel={() => setOpenConfirm(false)}
      />

    </Box>
  );
};

export default GuideVideos;